import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
    useGetKycStagesQuery,
    useGetKycSummaryQuery,
    useUploadKycDocumentMutation,
    getHttpStatus,
} from '../../api';
import { KycStage } from '../../api/kyc/types';
import { IS_KYC_ENABLED } from '../../api/utils';
import { useTranslation } from '../../hooks/useTranslation';
import Button from '../../components/base/Button';
import PageTitle from '../../components/base/PageTitle';
import KycItemCard from '../../components/kyc/KycItemCard';
import { getKycFileError, isKycDocumentType } from '../../components/kyc/kycMeta';
import { alertManager } from '../../utils/alertManager';
import { KYC_SUCCESS_ROUTE } from './kycRoutes';

const KycDocumentsPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const stagesQuery = useGetKycStagesQuery(undefined, {
        skip: !IS_KYC_ENABLED,
        refetchOnMountOrArgChange: true,
    });
    const summaryQuery = useGetKycSummaryQuery(undefined, {
        skip: !IS_KYC_ENABLED,
        refetchOnMountOrArgChange: true,
    });
    const [upload] = useUploadKycDocumentMutation();
    const [uploadingId, setUploadingId] = useState<number | null>(null);
    const uploadPending = useRef(false);
    const { refetch: refetchStages } = stagesQuery;
    const { refetch: refetchSummary } = summaryQuery;

    useEffect(() => {
        if (!IS_KYC_ENABLED) return;
        const refresh = () => {
            if (document.visibilityState === 'hidden') return;
            void refetchStages();
            void refetchSummary();
        };
        const timer = window.setInterval(refresh, 15000);
        document.addEventListener('visibilitychange', refresh);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', refresh);
        };
    }, [refetchStages, refetchSummary]);

    if (!IS_KYC_ENABLED) return <Navigate to="/profile" replace />;
    const refresh = () => {
        void refetchStages();
        void refetchSummary();
    };
    const handleUpload = async (stage: KycStage, file: File) => {
        if (uploadPending.current || !stage.can_upload || !isKycDocumentType(stage.type)) return;
        const error = getKycFileError(file);
        if (error) {
            alertManager.error(t.kyc.errors[error]);
            return;
        }
        uploadPending.current = true;
        setUploadingId(stage.id ?? -1);
        try {
            await upload({ type: stage.type, file }).unwrap();
            alertManager.success(t.kyc.uploadSaved);
        } catch (error) {
            const status = getHttpStatus(error);
            alertManager.error(
                status === 413
                    ? t.kyc.errors.largeFile
                    : status === 400
                      ? t.kyc.errors.invalidFile
                      : status === 409
                        ? t.kyc.errors.conflict
                        : t.kyc.errors.upload,
            );
        } finally {
            uploadPending.current = false;
            setUploadingId(null);
        }
    };
    const stages = stagesQuery.data;
    const summary = summaryQuery.data;
    const progress =
        summary && summary.total_required > 0
            ? Math.max(
                  0,
                  Math.min(100, Math.round((summary.completed_required / summary.total_required) * 100)),
              )
            : 0;
    const loading = stagesQuery.isLoading || summaryQuery.isLoading;
    const error = stagesQuery.isError || summaryQuery.isError;
    const documents = (stages || []).filter((stage) =>
        ['kbo', 'company_charter', 'ubo', 'annual_report'].includes(stage.type || ''),
    );
    const identity = (stages || []).filter(
        (stage) => stage.type === 'admin_id' || stage.type === 'beneficiaries',
    );
    const other = (stages || []).filter((stage) => !documents.includes(stage) && !identity.includes(stage));
    const sections = [
        { title: t.kyc.documents, stages: documents },
        { title: t.kyc.identity, stages: identity },
        { title: t.kyc.otherChecks, stages: other },
    ];
    return (
        <Flex flex={1} minH={0} direction="column" overflow="hidden" bg="gray.100" px={4}>
            <Flex flexShrink={0} justify="center">
                <PageTitle title={t.kyc.title} />
            </Flex>
            <Flex
                flex={1}
                minH={0}
                w={{ base: 'full', md: '600px' }}
                maxW="4xl"
                mx="auto"
                overflowY="auto"
                direction="column"
            >
                <VStack gap={6} align="stretch" w="full" pt={2} pb={4} mb="100px">
                    <Box bg="white" p={{ base: 4, md: 5 }}>
                        <VStack align="stretch" gap={3}>
                            <HStack justify="space-between">
                                <Text fontSize="md" fontWeight="semibold">
                                    {t.kyc.progress}
                                </Text>
                                <Button
                                    buttonType="secondary"
                                    size="sm"
                                    onClick={refresh}
                                    disabled={stagesQuery.isFetching || summaryQuery.isFetching}
                                >
                                    {t.kyc.refresh}
                                </Button>
                            </HStack>
                            {summary && (
                                <>
                                    <Text fontSize="sm" color="gray.600">
                                        {t.kyc.progressCount
                                            .replace('{completed}', String(summary.completed_required))
                                            .replace('{total}', String(summary.total_required))}
                                    </Text>
                                    <Box bg="gray.100" h="6px">
                                        <Box bg="yellow.600" h="full" w={`${progress}%`} />
                                    </Box>
                                </>
                            )}
                            <Text fontSize="sm" color="gray.500">
                                {t.kyc.hint}
                            </Text>
                        </VStack>
                    </Box>
                    {loading && <Text role="status">{t.kyc.loading}</Text>}
                    {error && (
                        <Box bg="red.50" color="red.700" p={4} role="alert">
                            <Text>{t.kyc.errors.load}</Text>
                            <Button buttonType="secondary" onClick={refresh}>
                                {t.kyc.retry}
                            </Button>
                        </Box>
                    )}
                    {!stagesQuery.isLoading && stages && stages.length === 0 && (
                        <Box bg="white" p={5}>
                            <Text>{t.kyc.empty}</Text>
                        </Box>
                    )}
                    {sections.map(({ title, stages: sectionStages }) => {
                        if (!sectionStages.length) return null;
                        return (
                            <React.Fragment key={title}>
                                <Text
                                    fontSize="xl"
                                    fontWeight="bold"
                                    color="gray.800"
                                    fontFamily="'Oswald', sans-serif"
                                >
                                    {title}
                                </Text>
                                {sectionStages.map((stage, index) => (
                                    <KycItemCard
                                        key={stage.id ?? `${stage.type}-${index}`}
                                        stage={stage}
                                        isUploading={uploadingId === (stage.id ?? -1)}
                                        uploadDisabled={uploadingId !== null || stagesQuery.isFetching}
                                        onFileSelect={(file) => void handleUpload(stage, file)}
                                    />
                                ))}
                            </React.Fragment>
                        );
                    })}
                    {summary?.is_completed && !error && !loading && (
                        <Button
                            buttonType="success"
                            size="lg"
                            w="full"
                            onClick={() => navigate(KYC_SUCCESS_ROUTE)}
                        >
                            {t.kyc.continue}
                        </Button>
                    )}
                </VStack>
            </Flex>
        </Flex>
    );
};
export default KycDocumentsPage;
